import {
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgxsModule } from '@ngxs/store';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { provideToastr } from 'ngx-toastr';

import { routes } from '@app/app.routes';
import { baseHttpInterceptor } from '@app/core/interceptor';
import { provideBaseAppType, provideBaseUrl, provideStateLogoutPlugin } from '@app/core/provider';
import {
  AuthState,
  ClientConfigurationState,
  DamageAreaState,
  PhotoEstimationAuthState,
  PhotoEstimationLinkState,
  PreferredBodyshopsState,
  RepairJourneyState,
  StepImagesState,
  StepLocationState,
  SubmissionState,
  TermsAndConditionsState,
  YourDetailsState,
  YourVehicleState,
} from '@app/data/state';
import { isTokenExpired } from '@app/shared/utils/token.util';
import { environment } from '@env';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([baseHttpInterceptor])),
    provideAnimations(),
    provideToastr({ positionClass: 'toast-top-center', closeButton: true, progressBar: true }),
    provideBaseUrl(),
    provideBaseAppType(),
    provideStateLogoutPlugin(),
    importProvidersFrom(
      NgxsModule.forRoot(
        [
          AuthState,
          PhotoEstimationLinkState,
          PhotoEstimationAuthState,
          RepairJourneyState,
          YourVehicleState,
          YourDetailsState,
          DamageAreaState,
          StepImagesState,
          StepLocationState,
          TermsAndConditionsState,
          PreferredBodyshopsState,
          SubmissionState,
          ClientConfigurationState,
        ],
        {
          developmentMode: !environment.production,
          selectorOptions: {
            suppressErrors: false,
            injectContainerState: false,
          },
        }
      ),
      NgxsStoragePluginModule.forRoot({
        // The link state is persisted alongside the token because `linkValidGuard` reads it on every
        // navigation. Held in memory only, a refresh - or a mobile browser reclaiming the tab while
        // the customer is in the camera, which is the whole point of this tool - left them
        // authenticated but locked out, and told their link had expired.
        keys: [AuthState, PhotoEstimationLinkState],
        afterDeserialize: (obj, key) => {
          if (
            key === 'auth' &&
            obj &&
            typeof obj === 'object' &&
            'token' in obj &&
            typeof (obj as { token: unknown }).token === 'string' &&
            (obj as { token: string }).token &&
            isTokenExpired((obj as { token: string }).token)
          ) {
            return null;
          }
          return obj;
        },
      }),
      NgxsLoggerPluginModule.forRoot({ disabled: environment.production }),
      NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production })
    ),
  ],
};
